/**
 * The cars one driver may drive, chosen by the office (#370).
 *
 * One chip per car, any number on: one driver may be allowed several cars, and one car is
 * shared by the day and the night driver. Saved as the whole list, so a change cannot leave
 * half of itself behind. A firm has fifty cars: the chosen ones come first, the rest by
 * plate, and a box narrows them by plate, make or model. A car retired from the road is not offered — it cannot be signed
 * into anyway — but one already allowed stays shown so it can be taken off.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Banner, Button, Chip, ChipGroup, Dialog, Input } from '../../ui';
import type { FleetDriver, FleetVehicle } from './api';

export function AllowedCarsDialog({
  driver,
  vehicles,
  busy,
  error,
  onSave,
  onClose,
}: {
  driver: FleetDriver;
  vehicles: FleetVehicle[];
  busy: boolean;
  error: string | null;
  onSave: (vehicleIds: string[]) => Promise<boolean>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<string[]>(driver.allowedVehicleIds ?? []);
  const [filter, setFilter] = useState('');

  // In the order they were when the dialog opened, so a chip does not jump away from the
  // pointer that just pressed it.
  const [first] = useState(() => new Set(driver.allowedVehicleIds ?? []));

  const needle = filter.trim().toLowerCase();

  const shown = vehicles
    .filter((vehicle) => vehicle.isActive || chosen.includes(vehicle.id))
    .filter(
      (vehicle) =>
        needle === '' ||
        `${vehicle.plate} ${vehicle.make} ${vehicle.model}`.toLowerCase().includes(needle),
    )
    .sort(
      (a, b) =>
        Number(first.has(b.id)) - Number(first.has(a.id)) || a.plate.localeCompare(b.plate),
    );

  const toggle = (id: string) =>
    setChosen((current) =>
      current.includes(id) ? current.filter((one) => one !== id) : [...current, id],
    );

  return (
    <Dialog
      open
      title={t('fleet.allowed.title', { name: driver.displayName })}
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t('fleet.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => void onSave(chosen).then((done) => done && onClose())}
          >
            {t('fleet.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error !== null && <Banner tone="danger">{error}</Banner>}

        <p className="m-0 text-secondary text-ink-3">{t('fleet.allowed.help')}</p>

        <Input
          aria-label={t('fleet.allowed.find')}
          placeholder={t('fleet.allowed.find')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />

        {shown.length === 0 ? (
          <p className="m-0 text-secondary text-ink-3">{t('fleet.vehicles.none')}</p>
        ) : (
          <ChipGroup label={t('fleet.allowed.cars')}>
            {shown.map((vehicle) => {
              const on = chosen.includes(vehicle.id);

              return (
                <Chip
                  key={vehicle.id}
                  current={on}
                  title={`${vehicle.make} ${vehicle.model}`}
                  onClick={() => toggle(vehicle.id)}
                >
                  <span className="font-mono">{vehicle.plate}</span>
                  <span className="ml-1.5 opacity-75">
                    {vehicle.make} {vehicle.model}
                  </span>
                </Chip>
              );
            })}
          </ChipGroup>
        )}
      </div>
    </Dialog>
  );
}
